import { Directive, ElementRef, inject, OnDestroy } from '@angular/core';
import { TargetType } from '@libs/shared/models';
import { LinkRouterService } from './router';

@Directive({
  selector: '[uiHtmlDataLink]',
})
export class HtmlDataLinkDirective implements OnDestroy {
  private readonly linkRouter: LinkRouterService;
  private readonly element: HTMLElement;
  private readonly mutationObserver?: MutationObserver;
  private readonly excludedLinkFormats: string[] = ['mailto:', 'tel:'];

  public constructor() {
    this.linkRouter = inject(LinkRouterService);
    this.element = inject(ElementRef).nativeElement as HTMLElement;

    this.mutationCallback();

    // istanbul ignore else
    if (typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(this.mutationCallback.bind(this));
      this.mutationObserver.observe(this.element, { childList: true, subtree: true });
    }
  }

  public ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
  }

  private mutationCallback(): void {
    // istanbul ignore if
    if (!this.element?.querySelectorAll) {
      return;
    }

    Array.from(this.element.querySelectorAll('a'))
      .forEach(anchor => void this.processAnchorElement(anchor));
  }

  private async processAnchorElement(anchor: HTMLAnchorElement): Promise<void> {
    // Avoid double processing by partial HTML updates
    if (anchor.getAttribute('data-processed-href')) {
      return;
    }

    const rawHref: string = String(anchor.getAttribute('href'));

    const excluded: boolean = this.excludedLinkFormats.some(x => rawHref.startsWith(x));

    if (excluded) {
      return;
    }

    const href: string = await this.linkRouter.formatHref(rawHref);

    anchor.setAttribute('data-processed-href', href);
    anchor.href = href;

    if (!anchor.target) {
      anchor.target = '_self';
    }

    anchor.addEventListener('click', event => void this.onClick(event, anchor));
  }

  private onClick(event: Event, link: HTMLAnchorElement): void {
    event.preventDefault();
    event.stopPropagation();

    this.linkRouter.navigate(
      String(link.getAttribute('data-processed-href')),
      link.target as TargetType,
    );
  }
}
